// @figma file=pkCGby54zXzTsccXvHqzO6 node=3928:2374 name="Default Textarea" set="mytaible-shadcn-studio" verified=2026-03-22
import { Loader2, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Controls CSS resize behaviour on the textarea. */
export type TextareaResize = 'none' | 'vertical' | 'horizontal' | 'both';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Additional CSS classes merged onto the root element. */
  className?: string;

  /**
   * Controls the CSS `resize` behaviour.
   * @default 'vertical'
   */
  resize?: TextareaResize;

  /**
   * Content rendered inside the top-left corner of the field — typically an
   * icon such as `<MessageSquare className="size-4" />`. The textarea receives
   * extra left padding automatically.
   */
  startIcon?: React.ReactNode;

  /**
   * Content rendered inside the top-right corner of the field — typically an
   * icon or status glyph. Suppressed while `loading` is `true` or when the
   * clear button is visible.
   */
  endIcon?: React.ReactNode;

  /**
   * When `true`, renders a spinner at the top-right corner. Suppresses
   * `endIcon` and the clear button. Use during async operations.
   */
  loading?: boolean;

  /**
   * When `true`, shows an × button at the top-right corner whenever the field
   * has a non-empty controlled `value`. Clicking it calls `onChange` with
   * `event.target.value = ""`. Requires a controlled `value` prop to activate.
   * Suppressed while `loading` is true, or when the field is `disabled` /
   * `readOnly`.
   */
  clearable?: boolean;

  /**
   * ReactNode rendered inside the bottom-right corner of the textarea — typically
   * a send/submit icon button or a small labelled button. The textarea receives
   * extra bottom padding automatically to avoid text overlapping the button.
   */
  submitButton?: React.ReactNode;

  /**
   * When `true`, renders a character count below the textarea.
   * Pair with `maxLength` to show `count/max`; without it shows count only.
   * Works with both controlled (`value`) and uncontrolled inputs.
   */
  showCount?: boolean;
}

// ─── Resize class map ─────────────────────────────────────────────────────────

const resizeClass: Record<TextareaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Multi-line text input field wrapping the native HTML `<textarea>` element
 * with AvilaRoffe theme styling.
 *
 * Supports all native HTML textarea attributes including `rows`, `disabled`,
 * `readOnly`, `placeholder`, `value`, `onChange`, `id`, `name`, and `required`.
 * Forwards its ref to the underlying `<textarea>` element for form library
 * compatibility (react-hook-form, etc.).
 *
 * When icon slots (`startIcon`, `endIcon`) or `submitButton` are provided,
 * the textarea is wrapped in a relative container — the ref still points to
 * the inner `<textarea>`.
 *
 * Error state is driven by `aria-invalid={true}` — set this directly or let
 * `FormControl` / `FieldWrapper` inject it automatically.
 *
 * Figma: COMPONENT_SET `3928:2374` — "Default Textarea" (Filled=No, State=Default)
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      resize = 'vertical',
      rows = 3,
      startIcon,
      endIcon,
      loading = false,
      clearable = false,
      submitButton,
      showCount = false,
      onChange,
      value,
      maxLength,
      ...props
    },
    ref,
  ) => {
    // Track char count for uncontrolled usage
    const [internalCount, setInternalCount] = React.useState(
      typeof value === 'string' ? value.length : 0,
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInternalCount(e.target.value.length);
      onChange?.(e);
    };

    const showClear = clearable && !loading && !props.disabled && !props.readOnly && !!value;

    const handleClear = () => {
      setInternalCount(0);
      onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLTextAreaElement>);
    };

    const hasStart = !!startIcon;
    const hasEnd = !!(loading || clearable || endIcon);
    const hasSubmit = !!submitButton;
    const hasSlots = hasStart || hasEnd || hasSubmit;

    const charCount = typeof value === 'string' ? value.length : internalCount;

    const textareaCls = cn(
      // ── Layout ─────────────────────────────────────────────────────────────
      'flex w-full',
      // ── Colours & border ───────────────────────────────────────────────────
      'border border-input bg-input-bg px-3 py-2 text-base sm:text-sm text-foreground shadow-sm',
      // ── Border radius ──────────────────────────────────────────────────────
      'rounded-[min(var(--field-radius,var(--radius)),0.75rem)]',
      // ── Placeholder ────────────────────────────────────────────────────────
      'placeholder:text-muted-foreground',
      // ── Focus ring ─────────────────────────────────────────────────────────
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      // ── Disabled ───────────────────────────────────────────────────────────
      'disabled:cursor-not-allowed disabled:opacity-50',
      // ── ReadOnly ───────────────────────────────────────────────────────────
      'read-only:bg-muted read-only:cursor-default',
      // ── Error ──────────────────────────────────────────────────────────────
      'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
      // ── Resize ─────────────────────────────────────────────────────────────
      resizeClass[resize],
      // ── Slot padding ───────────────────────────────────────────────────────
      hasStart && 'pl-9',
      hasEnd && 'pr-9',
      hasSubmit && 'pb-11',
      className,
    );

    const textareaEl = (
      <textarea
        ref={ref}
        rows={rows}
        className={textareaCls}
        onChange={handleChange}
        value={value}
        maxLength={maxLength}
        {...props}
      />
    );

    const countEl = showCount ? (
      <p className="select-none text-right text-xs tabular-nums text-muted-foreground">
        {charCount}
        {maxLength != null ? `/${maxLength}` : ''} characters
      </p>
    ) : null;

    // ── No slots: bare textarea (original contract preserved) ─────────────────
    if (!hasSlots) {
      if (!showCount) return textareaEl;
      return (
        <div className="w-full space-y-1">
          {textareaEl}
          {countEl}
        </div>
      );
    }

    // ── Slots present: wrap in relative container ──────────────────────────────
    const slottedEl = (
      <div className="relative w-full">
        {/* ── Start slot — top-left ─────────────────────────────────────── */}
        {startIcon && (
          <span className="pointer-events-none absolute left-3 top-3 flex size-4 items-center text-muted-foreground">
            {startIcon}
          </span>
        )}

        {textareaEl}

        {/* ── End slot — top-right — priority: loading > clear > endIcon ── */}
        {loading ? (
          <span className="pointer-events-none absolute right-3 top-3 flex size-4 items-center text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </span>
        ) : showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            onClick={handleClear}
            className="absolute right-3 top-3 flex size-4 items-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none"
          >
            <X className="size-4" />
          </button>
        ) : endIcon ? (
          <span className="pointer-events-none absolute right-3 top-3 flex size-4 items-center text-muted-foreground">
            {endIcon}
          </span>
        ) : null}

        {/* ── Submit button — bottom-right ──────────────────────────────── */}
        {hasSubmit && <div className="absolute bottom-2.5 right-2.5">{submitButton}</div>}
      </div>
    );

    if (!showCount) return slottedEl;
    return (
      <div className="w-full space-y-1">
        {slottedEl}
        {countEl}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Textarea };
